import React, { useState } from 'react';
import Header from '../components/layout/Header';
import Sidebar from '../components/layout/Sidebar';
import RightBar from '../components/layout/RightBar';
import CommunityList from '../components/communities/CommunityList';
import CommunityForm from '../components/communities/CommunityForm';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import useAuth from '../hooks/useAuth';

/**
 * Communities page for browsing and creating communities
 * @returns {JSX.Element} Communities page component
 */
const Communities = () => {
  const { isAuth } = useAuth();
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState('-createdAt');
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Available categories
  const categories = [
    'Technology',
    'Business',
    'Health',
    'Science',
    'Education',
    'Entertainment',
    'Sports',
    'Gaming',
    'Arts',
    'Food',
    'Travel',
    'Finance',
    'Fashion',
    'Photography',
    'Music',
    'Other',
  ];
  
  // Available sort options
  const sortOptions = [
    { value: '-createdAt', label: 'Newest' },
    { value: 'createdAt', label: 'Oldest' },
    { value: '-memberCount', label: 'Most popular' },
    { value: 'name', label: 'Name (A-Z)' },
    { value: '-name', label: 'Name (Z-A)' },
  ];
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };
  
  // Handle category change
  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  };
  
  // Handle sort change
  const handleSortChange = (e) => {
    setSortBy(e.target.value);
  };
  
  // Handle filter change
  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };
  
  // Handle search form submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    // The filters are reactive, so no additional action needed
  };
  
  // Handle community creation success
  const handleCommunityCreated = (newCommunity) => {
    setShowCreateModal(false);
    // Refresh communities list would happen automatically
  };
  
  // Get filter parameters based on active filter and search inputs
  const getFilterParams = () => {
    const params = {
      sort: sortBy,
    };
    
    if (searchQuery) {
      params.search = searchQuery;
    }
    
    if (selectedCategory) {
      params.category = selectedCategory;
    }
    
    // Apply filter for user's communities
    if (activeFilter === 'my-communities' && isAuth) {
      params.member = true;
    }
    
    return params;
  };
  
  return (
    <>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Left sidebar */}
          <div className="hidden md:block md:w-64 lg:w-72">
            {/* <Sidebar /> */}
          </div>
          
          {/* Main content */}
          <div className="flex-1 space-y-6">
            {/* Page header */}
            <div className="flex justify-between items-center">
              <h1 className="text-2xl font-bold text-gray-900">Communities</h1>
              
              {isAuth && (
                <Button
                  variant="primary"
                  onClick={() => setShowCreateModal(true)}
                >
                  Create Community
                </Button>
              )}
            </div>
            
            {/* Filters */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              {/* Tabs */}
              <div className="flex border-b border-gray-200 mb-4">
                <button
                  className={`px-4 py-2 text-sm font-medium ${
                    activeFilter === 'all'
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  onClick={() => handleFilterChange('all')}
                >
                  All Communities
                </button>
                
                {isAuth && (
                  <button
                    className={`px-4 py-2 text-sm font-medium ${
                      activeFilter === 'my-communities'
                        ? 'text-blue-600 border-b-2 border-blue-600'
                        : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                    onClick={() => handleFilterChange('my-communities')}
                  >
                    My Communities
                  </button>
                )}
              </div>
              
              {/* Search and filters */}
              <form onSubmit={handleSearchSubmit}>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* Search input */}
                  <div>
                    <div className="relative rounded-md shadow-sm">
                      <input
                        type="text"
                        className="block w-full pr-10 p-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="Search communities..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <svg
                          className="h-5 w-5 text-gray-400"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  {/* Category filter */}
                  <div>
                    <select
                      className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      value={selectedCategory}
                      onChange={handleCategoryChange}
                    >
                      <option value="">All Categories</option>
                      {categories.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Sort filter */}
                  <div>
                    <select
                      className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      value={sortBy}
                      onChange={handleSortChange}
                    >
                      {sortOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </form>
            </div>
            
            {/* Communities list */}
            <CommunityList
              filters={getFilterParams()}
              layout="grid"
              showPagination={true}
            />
          </div>
          
          {/* Right sidebar */}
          <div className="hidden lg:block lg:w-80">
            <RightBar />
          </div>
        </div>
      </div>
      
      {/* Create community modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create a Community"
        size="2xl"
      >
        <CommunityForm
          onSuccess={handleCommunityCreated}
          onCancel={() => setShowCreateModal(false)}
        />
      </Modal>
    </>
  );
};

export default Communities;